/**
 * Mobile-Only Dot Pagination for Product Gallery
 * Adds pagination dots on mobile without affecting desktop
 */
if (!customElements.get('mobile-gallery-dots')) {
  customElements.define('mobile-gallery-dots', class extends HTMLElement {
    constructor() {
      super();
      this.isChanging = false;
      this.mediaGallery = null;
      this.sliderComponent = null;
      this.initialized = false;
      this.observer = null;
      
      // Pre-bind handlers for better performance
      this.dotContainerClickHandler = this.dotContainerClickHandler.bind(this);
    }
    
    // Debounce helper function
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
    
    dotContainerClickHandler(e) {
      const dot = e.target.closest('[data-action="select-media"]');
      if (!dot || this.isChanging) return;
      
      const mediaId = dot.dataset.target;
      const gallery = this.mediaGallery;
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(mediaId, false);
      }
    }
    
    connectedCallback() {
      // Only operate on mobile
      if (window.innerWidth >= 750) return;
      
      // Cache DOM elements
      this.mediaGallery = document.querySelector('media-gallery');
      if (!this.mediaGallery) return;
      
      this.sliderComponent = this.mediaGallery.querySelector('slider-component');
      if (!this.sliderComponent) return;
      
      // Event listeners with bound context and debounce
      this.handleVariantChangeBound = this.debounce(this.handleVariantChange.bind(this), 50);
      this.handleSlideChangeBound = this.handleSlideChange.bind(this);
      this.handleFormInputChangeBound = this.handleFormInputChange.bind(this);
      
      // Use IntersectionObserver to only render when visible
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.initialized) {
            this.initialized = true;
            this.addDotPagination();
          }
        });
      }, { threshold: 0.1 });
      
      this.observer.observe(this.mediaGallery);
      
      // Add event listeners with passive option
      document.addEventListener('variant:changed', this.handleVariantChangeBound, { passive: true });
      document.addEventListener('change', this.handleFormInputChangeBound, { passive: true });
      this.sliderComponent.addEventListener('slideChanged', this.handleSlideChangeBound, { passive: true });
    }
    
    disconnectedCallback() {
      // Clean up event listeners
      document.removeEventListener('variant:changed', this.handleVariantChangeBound);
      document.removeEventListener('change', this.handleFormInputChangeBound);
      if (this.sliderComponent) {
        this.sliderComponent.removeEventListener('slideChanged', this.handleSlideChangeBound);
      }
      
      // Disconnect observer
      if (this.observer) {
        this.observer.disconnect();
      }
    }
    
    handleFormInputChange(e) {
      if (e.target.closest('.quantity__input') || e.target.closest('[name="quantity"]')) return;
      if (e.target.closest('.product-form__input')) this.handleVariantChange();
    }
    
    addDotPagination() {
      // Cache references
      const { mediaGallery, sliderComponent } = this;
      if (!mediaGallery || !sliderComponent) return;
      
      // Remove any existing pagination
      const existingPagination = mediaGallery.querySelector('.gallery-dot-pagination');
      if (existingPagination) existingPagination.remove();
      
      // Get only visible media items
      const mediaItems = Array.from(mediaGallery.querySelectorAll('.product__media-item')).filter(
        item => !item.classList.contains('filtered-variant')
      );
      
      // Don't add pagination for single image
      if (mediaItems.length <= 1) return;
      
      // Find active media
      let activeMediaId = null;
      for (let i = 0; i < mediaItems.length; i++) {
        if (mediaItems[i].classList.contains('is-active')) {
          activeMediaId = mediaItems[i].getAttribute('data-media-id');
          break;
        }
      }
      
      // If no active media found, use the first one
      if (!activeMediaId && mediaItems.length > 0) {
        activeMediaId = mediaItems[0].getAttribute('data-media-id');
      }
      
      // Create elements using fragment
      const fragment = document.createDocumentFragment();
      const pagination = document.createElement('div');
      pagination.className = 'gallery-dot-pagination';
      pagination.setAttribute('role', 'tablist');
      
      const dotContainer = document.createElement('div');
      dotContainer.className = 'dot-container';
      
      // Add event delegation to container
      dotContainer.addEventListener('click', this.dotContainerClickHandler, { passive: true });
      
      // Use a document fragment for better performance
      for (let i = 0; i < mediaItems.length; i++) {
        const item = mediaItems[i];
        const mediaId = item.getAttribute('data-media-id');
        const isActive = mediaId === activeMediaId;
        
        const dot = document.createElement('button');
        dot.className = isActive ? 'dot is-active' : 'dot';
        dot.setAttribute('data-target', mediaId);
        dot.setAttribute('data-media-position', i + 1);
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', isActive);
        dot.setAttribute('aria-current', isActive);
        dot.dataset.action = 'select-media';
        
        dotContainer.appendChild(dot);
      }
      
      pagination.appendChild(dotContainer);
      fragment.appendChild(pagination);
      
      // Add pagination after slider - single DOM update
      sliderComponent.after(fragment);
    }
    
    handleVariantChange() {
      if (this.isChanging || window.innerWidth >= 750 || !this.mediaGallery) return;
      this.isChanging = true;
      
      // Fade out dots during variant change
      const dotPagination = this.mediaGallery.querySelector('.gallery-dot-pagination');
      if (dotPagination) {
        dotPagination.classList.add('variant-changing');
        dotPagination.classList.add('is-hidden');
      }
      
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.addDotPagination();
          
          const newDotPagination = this.mediaGallery.querySelector('.gallery-dot-pagination');
          if (newDotPagination) {
            newDotPagination.classList.add('variant-changing');
            newDotPagination.classList.add('is-hidden');
            
            // Use requestAnimationFrame for smoother transitions
            requestAnimationFrame(() => {
              setTimeout(() => {
                newDotPagination.classList.remove('is-hidden');
                
                setTimeout(() => {
                  newDotPagination.classList.remove('variant-changing');
                  this.isChanging = false;
                }, 350);
              }, 450);
            });
          } else {
            this.isChanging = false;
          }
        }, 250);
      });
    }
    
    handleSlideChange(event) {
      if (window.innerWidth >= 750 || this.isChanging || !this.mediaGallery) return;
      
      const mediaId = event.detail?.currentElement?.dataset?.mediaId;
      if (!mediaId) return;
      
      // Update active dot more efficiently
      const dots = this.mediaGallery.querySelectorAll('.gallery-dot-pagination .dot');
      for (let i = 0; i < dots.length; i++) {
        const dot = dots[i];
        const isActive = dot.dataset.target === mediaId;
        
        // Only update if state is changing
        if ((dot.classList.contains('is-active') !== isActive)) {
          if (isActive) {
            dot.classList.add('is-active');
            dot.setAttribute('aria-selected', 'true');
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.classList.remove('is-active');
            dot.setAttribute('aria-selected', 'false');
            dot.setAttribute('aria-current', 'false');
          }
        }
      }
    }
  });
  
  // Initialize once on DOM ready with requestIdleCallback if available
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      if (!document.querySelector('mobile-gallery-dots')) {
        document.body.appendChild(document.createElement('mobile-gallery-dots'));
      }
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (!document.querySelector('mobile-gallery-dots')) {
        document.body.appendChild(document.createElement('mobile-gallery-dots'));
      }
    });
  }
}